import React, {useEffect, useState, ReactElement} from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import ToolbarNotificationService, {
  ToolbarNotificationShape,
} from '../../../services/ToolbarNotificationService';
import {useBoolean} from '../../../hooks/useBoolean';
import {Form, Formik} from 'formik';
import FieldComponent from '../../../components/FieldComponent';
import Button from '../../../components/button';
import LoadingIcon from 'shared/components/loading';
import {currentSchoolYear, US_ABBV_MAP} from '../../../../shared/utils';
import styles from './styles.module.scss';
import {errorMessage, flashMessage} from '../../../../shared/actions/notifications';
import {useDispatch} from 'react-redux';
import {EducationPhase} from '../../../types/enums';
import ToolbarNotificationPreviewForForm from '../../../components/toolbar-notification/toolbar-notification-preview-for-form';

const platformOptions = ['BASIC', 'IONIC'];
const educationPhaseOptions = [
  EducationPhase.HIGH_SCHOOL,
  EducationPhase.COMMUNITY_COLLEGE,
  EducationPhase.ADULT_LEARNER,
  EducationPhase.UNDERGRADUATE,
];

const userGroupOptions = ['Student', 'Educator', 'CollegePortalUser'];

const stateOptions = Object.keys(US_ABBV_MAP).map((stateAbbreviation) => ({
  _id: stateAbbreviation,
  name: US_ABBV_MAP[stateAbbreviation],
}));

const generatePossibleGradYears = (startYear): string[] =>
  Array.from({length: 11}, (_, i) => (startYear + i).toString());
const gradYearOptions = generatePossibleGradYears(currentSchoolYear() - 5);

const userAgeOptions = ['13', '14', '15', '16', '17', '18', '19', '20', '21', '22+'];

export const fields = [
  {type: 'textbox', name: 'identifier', label: 'Name', required: true},
  {type: 'textbox', name: 'htmlContent', label: 'HTML Content', required: true},
  {type: 'checkbox', name: 'active', label: 'Active'},
  {type: 'number', name: 'priority', label: 'Priority (1 - 4)', min: 0, max: 4},
  {
    type: 'multi-select',
    name: 'includedUserGroups',
    label: 'User Types',
    options: userGroupOptions,
  },
  {type: 'multi-select', name: 'includedPlatforms', label: 'Platforms', options: platformOptions},
  {
    type: 'multi-select',
    name: 'includedEducationPhases',
    label: 'Education Phases',
    options: educationPhaseOptions,
  },
  {
    type: 'multi-select',
    name: 'includedHighSchoolStates',
    label: 'High School States',
    options: stateOptions,
  },
  {type: 'multi-select', name: 'includedGradYears', label: 'Grad Years', options: gradYearOptions},
  {type: 'multi-select', name: 'includedAges', label: 'Student Ages', options: userAgeOptions},
  {
    type: 'multi-select',
    name: 'includedStates',
    label: 'User Location State',
    options: stateOptions,
  },
];

const emptyToolbarNotification = {
  _id: '',
  _type: '',
  active: false,
  createdAt: '',
  updatedAt: '',
  lastUpdatedBy: '',
  includedPlatforms: [],
  htmlContent:
    '<p class="toolbar-notification-styling">Click <a href="put-a-link-here" style="color:red">Here</a></p>',
  identifier: '',
  includedAges: [],
  includedGradYears: [],
  includedHighSchoolStates: [],
  includedStates: [],
  owners: [],
  priority: 0,
  includedUserGroups: [],
};

const CreateOrEditToolbarNotificationForm = ({match}: RouteComponentProps): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();

  const id = match.params['id'];
  const createMode = useBoolean(id === 'new');
  const loading = useBoolean(true);
  const saving = useBoolean(false);
  const [fetchedToolbarNotification, setFetchedToolbarNotification] =
    useState<ToolbarNotificationShape>(emptyToolbarNotification);

  useEffect(() => {
    if (createMode.value) {
      loading.setFalse();
    } else {
      ToolbarNotificationService.show(id)
        .then(setFetchedToolbarNotification)
        .then(loading.setFalse)
        .catch((error) => {
          if (error.status === 404) {
            console.error(
              `Toolbar Notification with id ${fetchedToolbarNotification._id} was not found.`
            );
            setFetchedToolbarNotification(emptyToolbarNotification);
          }
        });
    }
  }, []);

  const onSubmit = async (values: ToolbarNotificationShape): Promise<void> => {
    saving.setTrue();

    try {
      if (createMode.value) {
        const created = await ToolbarNotificationService.create(values);
        createMode.setFalse();
        history.push(`/admin/tools/toolbar-notifications/${created._id}`);
      } else {
        await ToolbarNotificationService.update(id, values);
      }
      dispatch(flashMessage('Success! saved.'));
    } catch {
      dispatch(
        errorMessage('Error: your notification could not be saved, please refresh and try again.')
      );
    } finally {
      saving.setFalse();
    }
  };

  if (loading.value) {
    return <div>Loading...</div>;
  }

  if (!fetchedToolbarNotification) {
    return <div>Toolbar Notification with id {match.params['id']} does not exist. </div>;
  }

  return (
    <div className={styles.bottomOfFormPadding}>
      <h1 className="shared-font headline">
        {createMode.value ? 'Creating' : 'Editing'} Toolbar Notification
      </h1>
      <div>
        <h2 className="shared-font title"> Usage Notes: </h2>
        <ul>
          <li className="shared-font body">
            Use inline styling when creating and styling the HTML content, for example: <br />
            <pre>
              <code>
                {'<p style="margin-top:10px">Click <a href="#" style="color:red">Here</a></p>'}
              </code>
            </pre>
          </li>
        </ul>
        <h2 className="shared-font title"> Past Editors: </h2>
        <span className="shared-font subheading">
          {fetchedToolbarNotification.owners.join(', ')}
        </span>
      </div>
      <Formik
        initialValues={fetchedToolbarNotification}
        onSubmit={(values): Promise<void> => onSubmit(values)}
      >
        {(): ReactElement => (
          <>
            <div>
              <h2 className="shared-font title"> Notification Preview: </h2>
              <ToolbarNotificationPreviewForForm />
            </div>
            <Form>
              {fields.map(({name, type, label, required, ...remaining}) => (
                <FieldComponent
                  key={name}
                  type={type}
                  name={name}
                  label={label}
                  required={!!required}
                  {...remaining}
                />
              ))}
              <Button disabled={saving.value} type="submit">
                {saving.value ? <LoadingIcon /> : 'Save'}
              </Button>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateOrEditToolbarNotificationForm;
